import type { RequestOptions } from '@@/plugin-request/request';
import type { RequestConfig } from '@umijs/max';
import { message, notification } from 'antd';
import { history } from '@umijs/max';
import {
  getAccessToken,
  isAccessTokenExpired,
  isRefreshTokenExpired,
  getRefreshToken,
  updateAccessToken,
  clearTokens,
} from '@/utils/tokenManager';
import { refreshToken as refreshTokenApi } from '@/services/app-api/auth';
import { saveTokens } from '@/utils/tokenManager';

// 用于存储正在进行的刷新请求，避免并发刷新
let refreshTokenPromise: Promise<string | null> | null = null;


/**
 * 刷新 access token
 * 使用 Promise 确保同一时间只有一个刷新请求
 */
async function doRefreshToken(): Promise<string | null> {
  const refreshTokenValue = getRefreshToken();
  if (!refreshTokenValue) {
    return null;
  }

  try {
    const response = await refreshTokenApi(refreshTokenValue);

    if (response.success && response.data) {
      const { accessToken, refreshToken, accessExpiresIn, refreshExpiresIn } = response.data;

      // 保存完整的 token 信息
      saveTokens({
        accessToken,
        refreshToken,
        accessExpiresIn,
        refreshExpiresIn,
      });
      // updateAccessToken(accessToken, accessExpiresIn);
      return accessToken;
    }
    return null;
  } catch (error) {
    console.error('刷新 token 失败:', error);
    return null;
  }
}

/**
 * 获取有效的 access token
 * 如果已过期则自动刷新
 */
async function getValidAccessToken(): Promise<string | null> {
  // 如果 refresh token 已过期，直接返回 null
  if (isRefreshTokenExpired()) {
    clearTokens();
    return null;
  }

  // 如果 access token 还有效，直接返回
  if (!isAccessTokenExpired()) {
    return getAccessToken();
  }

  // 如果已经有正在进行的刷新请求，等待它完成
  if (refreshTokenPromise) {
    return refreshTokenPromise;
  }

  // 开始刷新 token
  refreshTokenPromise = doRefreshToken();

  try {
    const newToken = await refreshTokenPromise;
    return newToken;
  } finally {
    // 无论成功失败，都清除刷新请求
    refreshTokenPromise = null;
  }
}

// 错误处理方案： 错误类型
enum ErrorShowType {
  SILENT = 0,
  WARN_MESSAGE = 1,
  ERROR_MESSAGE = 2,
  NOTIFICATION = 3,
  REDIRECT = 9,
}
// 与后端约定的响应数据格式
// interface ResponseStructure {
//   success: boolean;
//   data: any;
//   errorCode?: number;
//   errorMessage?: string;
//   showType?: ErrorShowType;
// }
interface ResponseStructure {
  success: boolean;
  data: any;
  message?: string;
  showType?: ErrorShowType;
}

/**
 * @name 错误处理
 * pro 自带的错误处理， 可以在这里做自己的改动
 * @doc https://umijs.org/docs/max/request#配置
 */
export const errorConfig: RequestConfig = {
  // 错误处理： umi@3 的错误处理方案。
  errorConfig: {
    // 错误抛出
    errorThrower: (res) => {
      const { success, data, message, showType } =
        res as unknown as ResponseStructure;

      // 仅 code === 200 认为成功，其它都按业务错误处理
      if (!success) {
        const error: any = new Error(message || '请求失败');
        error.name = 'BizError';
        error.info = {
          success,
          message,
          showType,
          data,
        };
        throw error; // 抛出自制的错误
      }
    },
    // 错误接收及处理
    errorHandler: (error: any, opts: any) => {
      if (opts?.skipErrorHandler) throw error;
      // 我们的 errorThrower 抛出的错误。
      if (error.name === 'BizError') {
        const errorInfo:
          | (ResponseStructure & {
            success?: boolean;
            message?: string;
          })
          | undefined = error.info;
        if (errorInfo) {
          const errMessage = errorInfo.message || '请求失败';
          const success = errorInfo.success;
          switch (errorInfo.showType) {
            case ErrorShowType.SILENT:
              // do nothing
              break;
            case ErrorShowType.WARN_MESSAGE:
              message.warning(errMessage);
              break;
            case ErrorShowType.ERROR_MESSAGE:
              message.error(errMessage);
              break;
            case ErrorShowType.NOTIFICATION:
              notification.open({
                description: errMessage,
                message: success ? '成功' : '错误',
              });
              break;
            case ErrorShowType.REDIRECT:
              // TODO: redirect
              break;
            default:
              message.error(errMessage);
          }
        }
      } else if (error.response) {
        // Axios 的错误
        // 请求成功发出且服务器也响应了状态码，但状态代码超出了 2xx 的范围
        const backendMessage = error.response?.data?.message;
        message.error(backendMessage || `Response status:${error.response.status}`);
      } else if (error.request) {
        // 请求已经成功发起，但没有收到响应
        // \`error.request\` 在浏览器中是 XMLHttpRequest 的实例，
        // 而在node.js中是 http.ClientRequest 的实例
        message.error('None response! Please retry.');
      } else {
        // 发送请求时出了点问题
        message.error('Request error, please retry.');
      }
    },
  },

  // 请求拦截器
  requestInterceptors: [
    async (config: RequestOptions) => {
      // 跳过刷新 token 接口本身，避免死循环
      if (config.url?.includes('/auth/refresh')) {
        const refreshTokenValue = getRefreshToken();
        if (refreshTokenValue) {
          config.headers = {
            ...config.headers,
            Authorization: `Bearer ${refreshTokenValue}`,
          };
        }
        return config;
      }

      // 获取有效的 access token（如果过期会自动刷新）
      const token = await getValidAccessToken();

      if (token) {
        config.headers = {
          ...config.headers,
          Authorization: `Bearer ${token}`,
        };
      } else {
        // 如果无法获取有效 token，且不在登录页面，跳转到登录页
        if (!window.location.pathname.includes('/user/login')) {
          clearTokens();
          history.push('/user/login');
        }
      }

      return config;
    },
  ],

  // 响应拦截器
  responseInterceptors: [
    (response) => {
      // 如果返回 401，表示 token 无效或已过期
      if (response.status === 401) {
        clearTokens();
        if (!window.location.pathname.includes('/user/login')) {
          message.error('登录已过期，请重新登录');
          history.push('/user/login');
        }
      }
      return response;
    },
  ],
};
